import { GameContext } from '../../../contexts/GameContext';
import { ImageWrapper } from '../../common/wrappers/ImageWrapper';
import { ZdarKidsGameStepConfiguration } from '../../../configuration/games/zdar-kids-game/zdarKidsGameContentConfiguration';
import { useLottie } from 'lottie-react';
import { zdarKidsCustomCiphers } from '../../../configuration/games/zdar-kids-game/zdarKidsGameCustomCiphers';
import React, { useContext } from 'react';
import ZdarBridgeCipher from './ciphers/ZdarBridgeCipher';
import ZdarChurchCipher from './ciphers/ZdarChurchCipher';
import ZdarFairyTaleCharactersCipher from './ciphers/ZdarFairyTaleCharactersCipher';
import ZdarPexesoCipher from './ciphers/ZdarPexesoCipher';
import ZdarPondCipher from './ciphers/ZdarPondCipher';
import parse from 'html-react-parser';
import squiggleAnimation from '../../../assets/images/games/zdar-kids-game/squiggle.json';

type Props = {
    onSuccess: () => void;
};
export default function ZdarKidsCipher({ onSuccess }: Props) {
    const { stepContent } = useContext(GameContext);
    const { cipher } = stepContent as ZdarKidsGameStepConfiguration;

    if (!cipher) {
        return <React.Fragment />;
    }

    const renderedCipher = cipher;

    const { images, text, custom, showSquiggle } = renderedCipher;

    const renderLabel = () => {
        if (renderedCipher.label) {
            return <div className="zdar-kids-cipher--label">{renderedCipher.label}</div>;
        } else {
            return <React.Fragment />;
        }
    };

    const renderImage = (i: number, image: any) => {
        return (
            <div key={i} className="cipher-image">
                <ImageWrapper src={image} alt="cipher" shouldScroll={false} allowLightbox={false} />
            </div>
        );
    };
    const renderCipher = () => {
        if (images?.length === 0) {
            return <div />;
        }
        return (
            <div className="cipher-wrapper">
                {renderLabel()}
                {images &&
                    images.map((image, i) => {
                        return renderImage(i, image);
                    })}
            </div>
        );
    };

    const renderText = () => {
        return (
            <div className="zdar-cipher-text-wrapper">
                {renderLabel()}
                <div className="zdar-cipher-text">{text && parse(text)}</div>
                {showSquiggle && <SquiggleImage />}
            </div>
        );
    };

    const renderCustom = () => {
        if (custom) {
            if (zdarKidsCustomCiphers.understairs === custom) {
                return (
                    <div className="zdar-cipher-text-wrapper">
                        {renderLabel()}
                        <ZdarFairyTaleCharactersCipher onSuccess={onSuccess} />
                    </div>
                );
            }
            if (zdarKidsCustomCiphers.pond === custom) {
                return (
                    <>
                        <div className="zdar-cipher-text-wrapper">
                            {renderLabel()}
                            <div className="zdar-cipher-text">
                                Najdi a zaklikej na obrázku schované věci které nepatří do rybníka.
                            </div>
                        </div>
                        <ZdarPondCipher onSuccess={onSuccess} />
                    </>
                );
            }
            if (zdarKidsCustomCiphers.bridge === custom) {
                return (
                    <>
                        <div className="zdar-cipher-text-wrapper">
                            {renderLabel()}
                            <div className="zdar-cipher-text">
                                Zaklikejte koho jste už potkali. Správné odpovědi po kliknutí změní barvu.
                            </div>
                        </div>
                        <ZdarBridgeCipher onSuccess={onSuccess} />
                    </>
                );
            }
            if (zdarKidsCustomCiphers.church === custom) {
                return (
                    <>
                        <div className="zdar-cipher-text-wrapper">
                            {renderLabel()}
                            <div className="zdar-cipher-text">
                                Co z daných věcí jsi v areálu kostela potkal? Správné odpovědi po kliknutí změní barvu.
                            </div>
                        </div>
                        <ZdarChurchCipher onSuccess={onSuccess} />
                    </>
                );
            }
            if (zdarKidsCustomCiphers.pexeso === custom) {
                return (
                    <>
                        <div className="zdar-cipher-text-wrapper">
                            {renderLabel()}
                            <div className="zdar-cipher-text">Vyřešte pexeso</div>
                        </div>
                        <ZdarPexesoCipher onSuccess={onSuccess} />
                    </>
                );
            }
        }

        return null;
    };

    const renderTextWithImages = () => {
        return (
            <>
                <div className="zdar-cipher-text-wrapper">
                    {renderLabel()}
                    <div className="zdar-cipher-text">{text && parse(text)}</div>
                </div>
                <div className="cipher-wrapper zdar-cipher-pathfinder zdar-cipher-pathfinder-with-text">
                    {images?.map((image, i) => {
                        return renderImage(i, image);
                    })}
                </div>
            </>
        );
    };
    if (text && images) {
        return renderTextWithImages();
    } else if (text) {
        return renderText();
    } else if (custom) {
        return renderCustom();
    }

    return renderCipher();
}

const SquiggleImage = () => {
    const options = {
        animationData: squiggleAnimation,
        loop: true,
        autoplay: true,
    };

    const { View } = useLottie(options);

    return View;
};
